import { CommonModule } from '@angular/common';
import { Component, HostListener, inject, input, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { UserOrganizationService } from '@http/clients/user-organization.service';
import { UserOrganization } from '@models/UserOrganization';
import { SideNavService } from '@services/side-nav.service';
import { UserOrganizationStorageService } from '@services/user-organization-storage.service';
import { OrganizationItemComponent } from '@shared/organization-item/organization-item.component';
import { IconSanitizerService, TooltipPosition } from 'ngx-lib';

@Component({
    selector: 'app-organization-switcher',
    templateUrl: './organization-switcher.component.html',
    styleUrls: ['./organization-switcher.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule, OrganizationItemComponent]
})
export class OrganizationSwitcherComponent implements OnInit {
    readonly userOrganizationDataService = inject(UserOrganizationService);
    readonly userOrganizationStorageService = inject(UserOrganizationStorageService);
    readonly router = inject(Router);
    readonly iconSanitizer = inject(IconSanitizerService);
    readonly sideNavService = inject(SideNavService);

    readonly selectedOrganization = input<UserOrganization | undefined>(undefined);
    readonly selectedOrganizationState = signal<UserOrganization | undefined>(undefined);
    readonly beforeOpenSelectedOrganization = signal<UserOrganization | undefined>(undefined);
    readonly opened = signal(false);
    readonly placeholder = 'Select an organization';
    readonly selectedOrganizationTooltipPosition = TooltipPosition.BELOW;
    readonly nextArrow: SafeHtml = this.iconSanitizer.getIcon('nextArrow');
    readonly close: SafeHtml = this.iconSanitizer.getIcon('close');
    organizations = signal<UserOrganization[] | null>(null);

    async ngOnInit(): Promise<void> {
        const organizations = await this.userOrganizationDataService.userOrganizationGetForCurrentUser();
        if (organizations.length > 0) {
            const mappedOrganizations = organizations.map((organization) =>
                UserOrganization.fromInterface(organization)
            );
            this.organizations.set(mappedOrganizations);

            const selectedOrg = this.userOrganizationStorageService.selectedOrganization();
            const resolvedSelectedOrganization =
                mappedOrganizations.find((org) => org.organizationId === selectedOrg?.id) ??
                this.selectedOrganization();
            this.selectedOrganizationState.set(resolvedSelectedOrganization);

            if (resolvedSelectedOrganization) {
                this.userOrganizationStorageService.updateUserOrganization({
                    id: resolvedSelectedOrganization.organizationId,
                    roleId: resolvedSelectedOrganization.userRoleId,
                    code: resolvedSelectedOrganization.organizationCode
                });
            }
        } else {
            this.selectedOrganizationState.set(this.selectedOrganization());
        }
    }

    openSwitcher(): void {
        this.beforeOpenSelectedOrganization.set(this.selectedOrganizationState());
        if (!this.opened()) this.opened.set(true);
    }

    selectItem(item: UserOrganization): void {
        if (this.selectedOrganizationState() !== item) {
            this.selectedOrganizationState.set(item);
            this.userOrganizationStorageService.updateUserOrganization({
                id: item.organizationId,
                roleId: item.userRoleId,
                code: item.organizationCode
            });
        }

        this.router.navigate(['/']);
        this.opened.set(false);
    }

    reloadComponent(): void {
        const currentUrl = this.router.url;

        if (this.urlMatchesCriteria(currentUrl)) {
            this.router.navigate(['/dashboard']);
        } else {
            this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                this.router.navigate([`/${currentUrl}`]);
            });
        }
    }

    urlMatchesCriteria(url: string): boolean {
        const criteria = ['-single', '/notifications', '/reports-', '/forbidden'];
        return criteria.some((criterion) => url.includes(criterion));
    }

    keyup(event: KeyboardEvent, item: UserOrganization) {
        if (event.key === 'Enter') {
            this.selectItem(item);
        }
    }

    closeSwitcher(): void {
        this.beforeOpenSelectedOrganization.set(this.selectedOrganizationState());
        if (this.opened()) this.opened.set(false);
    }

    @HostListener('document:click', ['$event'])
    onDocumentClicked(event: MouseEvent): void {
        if (this.opened() && (event.target as HTMLElement).classList.contains('panelWrapper')) {
            this.opened.set(false);
            this.selectedOrganizationState.set(this.beforeOpenSelectedOrganization());
        }
    }

    @HostListener('document:keydown', ['$event'])
    onDocumentKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape' && this.opened()) {
            this.opened.set(false);
            this.selectedOrganizationState.set(this.beforeOpenSelectedOrganization());
        }
    }
}

export interface IModalOrganizationParameters {
    title: string;
    applyButton: string;
    organizations?: UserOrganization[];
}
